/**
 * Wistia Video Embed Component
 *
 * This module handles the lazy-loading and customization of Wistia video players.
 */

// Type definitions for Wistia API
interface WistiaOptions {
    videoFoam: boolean;
    playerColor: string;
    controlsVisibleOnLoad: boolean;
    googleAnalytics: boolean;
    fitStrategy: string;
    preload: string;
    wmode: string;
    playButton?: boolean;
    popover?: boolean;
}

interface WistiaVideo {
    options: WistiaOptions;
    controls: {
        bigPlayButton: {
            buttonElem: HTMLElement;
            hiddenButton?: boolean;
        };
    };
    container: HTMLElement;
    play(): void;
    bind(event: string, callback: () => void): void;
    popover: {
        show(): void;
        thumbnail?: {
            bigPlayButton: {
                backgroundElem: HTMLElement;
                playElem: HTMLElement;
            };
        };
    };
}

interface WistiaQueueItem {
    id: string;
    options: WistiaOptions;
    onReady(video: WistiaVideo): void;
}

declare global {
    interface Window {
        _wq: WistiaQueueItem[];
    }
}

/**
 * Main video embed function that initializes lazy-loading for Wistia videos
 */
export default function videoEmbed(videoPlaceholders: NodeListOf<HTMLElement>) {
    videoPlaceholders.forEach(placeholder => {
        // Create an observer to lazy-load videos when they come into view
        const observer = createIntersectionObserver(placeholder);

        // Start observing the placeholder if it exists
        placeholder && observer.observe(placeholder);
    });

    /**
     * Creates an IntersectionObserver for lazy-loading videos
     */
    function createIntersectionObserver(placeholder: HTMLElement): IntersectionObserver {
        return new IntersectionObserver(
            (entries, observer) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        loadWistiaVideo(placeholder);
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                rootMargin: '300px 0px', // Begin loading when element is within 300px of viewport
                threshold: 0.1, // Trigger when at least 10% of element is visible
            }
        );
    }

    /**
     * Loads the Wistia video from the placeholder's data attribute
     */
    function loadWistiaVideo(placeholder: HTMLElement): void {
        // Get the Wistia embed code from the data attribute
        const wistiaSnippet = placeholder.getAttribute('data-wistia-snippet');

        // Create container for the video player
        const videoContainer = document.createElement('div');
        videoContainer.innerHTML = wistiaSnippet ?? '';

        // Replace placeholder with the actual video container
        placeholder.replaceWith(videoContainer);

        // Load all script tags from the Wistia embed code
        loadWistiaScripts(videoContainer);

        // Initialize Wistia customization
        enableWistiaVideoCustomization();
    }

    /**
     * Loads all script tags from the Wistia embed code
     */
    function loadWistiaScripts(container: HTMLElement): void {
        const scripts = container.querySelectorAll('script');
        scripts.forEach(script => {
            const newScript = document.createElement('script');
            newScript.src = script.src;
            newScript.async = true;
            document.body.appendChild(newScript);
        });
    }

    /**
     * Configures Wistia videos with custom options and behavior
     */
    function enableWistiaVideoCustomization(): void {
        // Initialize Wistia queue if it doesn't exist
        window._wq = window._wq || [];

        // Add configuration for all Wistia videos
        window._wq.push({
            id: '_all',
            options: getWistiaDefaultOptions(),
            onReady: handleWistiaVideoReady,
        });
    }

    /**
     * Returns default options for Wistia videos
     */
    function getWistiaDefaultOptions(): WistiaOptions {
        return {
            videoFoam: true,
            playerColor: getComputedStyle(document.documentElement).getPropertyValue(
                '--color--darkmid'
            ),
            controlsVisibleOnLoad: false,
            googleAnalytics: true,
            fitStrategy: 'cover',
            preload: 'none',
            wmode: 'transparent',
        };
    }

    /**
     * Handles the initialization of a Wistia video when it's ready
     */
    function handleWistiaVideoReady(video: WistiaVideo): void {
        const videoContainer = video.container.closest('.video-embed-wrapper');
        const customPlayButton = videoContainer?.querySelector(
            '[data-custom-play-button]'
        ) as HTMLElement;

        // Determine if this is a popover-type video
        const isPopover = detectPopoverVideo(video);

        // Configure play button behavior
        setupPlayButtonBehavior(video, customPlayButton, isPopover);

        // Add event listeners
        if (customPlayButton) {
            addCustomPlayButtonListener(video, customPlayButton, isPopover);
        }

        // Set up video state event handlers for non-popover videos
        if (!isPopover) {
            setupNonPopoverVideoEvents(video, customPlayButton);
        }
    }

    /**
     * Detects if a video is a popover-type video
     */
    function detectPopoverVideo(video: WistiaVideo): boolean {
        // Check both legacy and modern popover indicators
        return video.options.popover || typeof video.popover !== 'undefined';
    }

    /**
     * Sets up the play button behavior based on video configuration
     */
    function setupPlayButtonBehavior(
        video: WistiaVideo,
        customPlayButton: HTMLElement | null,
        isPopover: boolean
    ): void {
        if (!customPlayButton) return;

        if (video.options.playButton === false) {
            // If Wistia's native play button is disabled, hide our custom button too
            customPlayButton.style.display = 'none';
        } else {
            // Show our custom play button
            customPlayButton.classList.remove('tw:hidden');
            customPlayButton.style.display = 'grid';

            // Hide Wistia's default play button for all videos
            if (
                video.controls &&
                video.controls.bigPlayButton &&
                video.controls.bigPlayButton.buttonElem
            ) {
                video.controls.bigPlayButton.buttonElem.style.display = 'none';
                video.controls.bigPlayButton.hiddenButton = true; // This is a necessary workaround to hide the default Wistia play button in the above the fold area
            }

            // Popover videos, hide additional button elements - This applies only to modern popover video embeds (not the legacy ones)
            if (isPopover && video.popover.thumbnail) {
                hidePopoverThumbnailButtons(video);
            }
        }
    }

    /**
     * Hides the default play button elements in popover thumbnails
     */
    function hidePopoverThumbnailButtons(video: WistiaVideo): void {
        if (!video.popover || !video.popover.thumbnail) return;

        // Hide both background and play elements - This applies only to modern popover video embeds (not the legacy ones)
        if (video.popover.thumbnail.bigPlayButton) {
            if (video.popover.thumbnail.bigPlayButton.backgroundElem) {
                video.popover.thumbnail.bigPlayButton.backgroundElem.style.display =
                    'none';
            }
            if (video.popover.thumbnail.bigPlayButton.playElem) {
                video.popover.thumbnail.bigPlayButton.playElem.style.display = 'none';
            }
        }
    }

    /**
     * Adds click event listener to the custom play button
     */
    function addCustomPlayButtonListener(
        video: WistiaVideo,
        button: HTMLElement,
        isPopover: boolean
    ): void {
        button.addEventListener('click', function (e) {
            e.preventDefault();

            // For popover videos, show the popover first
            if (isPopover) {
                video.popover.show();
            }

            // Play the video
            video.play();
        });
    }

    /**
     * Sets up event handlers for non-popover videos to manage play button visibility
     */
    function setupNonPopoverVideoEvents(
        video: WistiaVideo,
        customPlayButton: HTMLElement | null
    ): void {
        if (!customPlayButton) return;

        // Hide custom play button when video starts playing
        video.bind('play', function () {
            customPlayButton.style.display = 'none';
        });

        // Show custom play button again when video ends
        video.bind('end', function () {
            customPlayButton.style.display = 'grid';
        });
    }
}
